import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CalendarPlus, Trash2, Eye } from "lucide-react";
import { CreateYearDialog } from "./CreateYearDialog";
import { DeleteYearDialog } from "./DeleteYearDialog";

// Gestione anni: scelta dell'anno da modificare, creazione, eliminazione, anno corrente
export const YearManager = ({ years, currentYear, selected, onSelect, onYearsChange }) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [nextCurrent, setNextCurrent] = useState(null);
  const label = (y) => `${y}${y === currentYear ? " (corrente)" : ""}`;

  const setCurrent = async () => {
    try {
      const { data } = await api.put("/admin/current-year", { year: nextCurrent });
      toast.success(`Anno corrente: ${data.current_year}`); onYearsChange(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setNextCurrent(null); }
  };

  return (
    <>
      <Card className="p-6 bg-card border-border space-y-4" data-testid="year-manager">
        <h3 className="text-xl">Anno scolastico {selected}</h3>
        <p className="text-sm text-muted-foreground">L'anno su cui lavori si sceglie dal selettore "Anno di lavoro" in alto. Qui crei, elimini e scegli l'anno visibile agli allievi.</p>
        <div className="flex flex-wrap gap-2 items-end">
          <Button variant="outline" data-testid="create-year-btn" onClick={() => setCreateOpen(true)}><CalendarPlus size={16} className="mr-1.5" /> Crea nuovo anno</Button>
          <Button variant="outline" data-testid="delete-year-btn" disabled={!selected || selected === currentYear}
            title={selected === currentYear ? "L'anno corrente non si può eliminare" : ""}
            onClick={() => setDeleteOpen(true)} className="text-destructive hover:text-destructive"><Trash2 size={16} className="mr-1.5" /> Elimina anno</Button>
        </div>
        {selected === currentYear && <p className="text-xs text-muted-foreground">L'anno corrente non si può eliminare.</p>}
        <div className="rounded-lg border border-border p-3 space-y-2" data-testid="current-year-box">
          <p className="text-sm font-semibold flex items-center gap-1.5"><Eye size={14} /> Anno corrente (visibile agli allievi)
            <Badge data-testid="current-year-badge" className="bg-primary/15 text-primary border-0">{currentYear}</Badge></p>
          <div className="flex flex-wrap gap-2 items-end">
            <Select value="" onValueChange={(y) => y !== currentYear && setNextCurrent(y)}>
              <SelectTrigger data-testid="current-year-select" className="bg-white w-48"><SelectValue placeholder="Cambia anno corrente" /></SelectTrigger>
              <SelectContent>{years.filter((y) => y !== currentYear).map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </Card>
      <CreateYearDialog open={createOpen} onOpenChange={setCreateOpen} years={years} currentYear={currentYear} onCreated={(d) => { onYearsChange(d); onSelect(d.year); }} />
      <DeleteYearDialog year={selected} open={deleteOpen} onOpenChange={setDeleteOpen} onDeleted={(d) => { onYearsChange(d); onSelect(d.current_year); }} />
      <AlertDialog open={!!nextCurrent} onOpenChange={(o) => !o && setNextCurrent(null)}>
        <AlertDialogContent data-testid="current-year-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rendere {nextCurrent} l'anno corrente?</AlertDialogTitle>
            <AlertDialogDescription>Da subito gli allievi vedranno l'anno {nextCurrent} nella loro area (prezzi, corsi e iscrizione di quell'anno). Nessun allievo viene spostato e nessuna email viene inviata.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="current-year-confirm" onClick={setCurrent}>Conferma</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
